import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { notifications } from '@mantine/notifications';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import { useDealRepository } from '../../../rpc';

/** Переводит доменные коды ошибок DealRepository.duplicate в человекочитаемые сообщения. */
function mapDuplicateError(code: string): string {
  switch (code) {
    case 'INVALID_SOURCE_DEAL_ID':
      return 'Некорректный ID исходной сделки';
    case 'SOURCE_DEAL_NOT_FOUND':
      return 'Исходная сделка не найдена';
    case 'NO_INITIAL_STATUS_FOR_TYPE':
      return 'Не найден стартовый статус для типа сделки — обратитесь к администратору';
    default:
      return code || 'Не удалось скопировать сделку';
  }
}

/**
 * Хук «Создать копию сделки».
 * Возвращает обработчик: confirm → DealRepository.duplicate → переход на новую сделку.
 * Ошибки маппируются в локализованные сообщения и показываются через notifications.
 *
 * Принимает `dealId: number | null`, чтобы вызов оставался безусловным
 * (правило хуков) даже если страница ещё не знает id сделки. При невалидном
 * id возвращаемый колбэк — no-op.
 */
export function useDuplicateDeal(dealId: number | null): () => Promise<void> {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const dealRepository = useDealRepository();

  return useCallback(async () => {
    if (dealId == null || !Number.isInteger(dealId) || dealId <= 0) return;
    const ok = await confirm(
      'Будут перенесены: партнёр, ответственный, описание, валюта, товары и услуги, work-orders. Новая сделка получит стартовый статус того же типа.',
      { title: 'Создать копию сделки', confirmLabel: 'Создать копию' }
    );
    if (!ok) return;
    try {
      const result = await dealRepository.duplicate(dealId);
      notifications.show({ color: 'green', message: 'Создана копия сделки' });
      navigate(`/crm/deals/${result.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка копирования сделки';
      notifications.show({ color: 'red', message: mapDuplicateError(message) });
    }
  }, [confirm, dealRepository, dealId, navigate]);
}
